import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const rentalForm = productGrid.querySelector('.rental-product-form-block');
      const rentalBuyoutForm = productGrid.querySelector('.rental-buyout-form-block');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (rentalForm) {
        productGrid.appendChild(rentalForm);
      }
      if (rentalBuyoutForm) {
        productGrid.appendChild(rentalBuyoutForm);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#executeInsertedScripts(modalContent);
    initializeXfRentalQuickAdd(modalContent);
  }

  /**
   * Re-runs inline scripts that were inserted through morphing fetched product HTML.
   * @param {Element} container
   */
  #executeInsertedScripts(container) {
    const scripts = container.querySelectorAll('script:not([type="application/json"])');

    scripts.forEach((script) => {
      const nextScript = document.createElement('script');

      Array.from(script.attributes).forEach((attribute) => {
        nextScript.setAttribute(attribute.name, attribute.value);
      });

      nextScript.textContent = script.textContent;
      script.replaceWith(nextScript);
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.rentalProduct === 'true') {
      this.setAttribute('data-quick-add-button', 'choose');
      return;
    }
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

function parseXfJson(root, selector, fallback) {
  const dataEl = root.querySelector(selector);
  if (!dataEl) return fallback;

  try {
    return JSON.parse(dataEl.textContent);
  } catch (error) {
    return fallback;
  }
}

function formatXfDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getXfTodayString() {
  return formatXfDate(new Date());
}

function parseXfMonthsFromLabel(label) {
  if (!label) return 1;

  const rangeMatch = label.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (rangeMatch?.[2]) return Number(rangeMatch[2]);

  const singleMatch = label.match(/(\d+)/);
  if (singleMatch?.[1]) return Number(singleMatch[1]);

  return 1;
}

function showXfRentalMessage(root, selector, text, type = 'info') {
  const message = root.querySelector(selector);
  if (!message) return;

  message.hidden = false;
  message.textContent = text;
  message.dataset.type = type;
}

function hideXfRentalMessage(root, selector) {
  const message = root.querySelector(selector);
  if (!message) return;

  message.hidden = true;
  message.textContent = '';
}

function getXfRentalQuantity(root) {
  const quantityInput = root.querySelector('[data-rental-quantity]');
  const value = Number(quantityInput?.value || 1);
  return Math.max(1, value);
}

function updateXfRentalEndDate(root, force = false) {
  const startDateInput = root.querySelector('[data-rental-start-date]');
  const endDateInput = root.querySelector('[data-rental-end-date]');
  const rentalDurationSelect = root.querySelector('[data-rental-duration-select]');

  if (!startDateInput?.value || !endDateInput || !rentalDurationSelect) return;
  if (endDateInput.value && !force) return;

  const selectedOption = rentalDurationSelect.options[rentalDurationSelect.selectedIndex];
  const durationLabel = selectedOption?.dataset.durationLabel || selectedOption?.textContent?.trim() || '';
  const endDate = new Date(`${startDateInput.value}T00:00:00`);
  endDate.setMonth(endDate.getMonth() + parseXfMonthsFromLabel(durationLabel));
  endDateInput.value = formatXfDate(endDate);
}

function updateXfRentalPrice(root) {
  const priceMain = root.querySelector('[data-rental-price]');
  const rentalDurationSelect = root.querySelector('[data-rental-duration-select]');
  const selectedOption = rentalDurationSelect?.options[rentalDurationSelect.selectedIndex];

  if (priceMain && selectedOption?.dataset.priceFormatted) {
    priceMain.textContent = selectedOption.dataset.priceFormatted;
  }
}

function initializeXfRentalProductForm(root) {
  const data = parseXfJson(root, '[data-rental-variants-json]', { variants: [] });
  const rentalDurationSelect = root.querySelector('[data-rental-duration-select]');
  const currentPrimaryValue = root.dataset.currentPrimaryValue || '';

  root.querySelectorAll('[data-rental-start-date], [data-rental-end-date]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.min = getXfTodayString();
    }
  });

  if (rentalDurationSelect instanceof HTMLSelectElement && !rentalDurationSelect.options.length) {
    const variants = data.variants.filter((variant) => {
      if (variant.isBuyout) return false;
      if (!currentPrimaryValue) return true;
      return variant.primaryValue === currentPrimaryValue;
    });

    variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.durationValue;
      option.dataset.durationLabel = variant.durationValue;
      option.dataset.primaryValue = variant.primaryValue || '';
      option.dataset.sellingPlanId = variant.sellingPlanId || '';
      option.dataset.priceFormatted = variant.priceFormatted || '';
      if (variant.durationValue === data.defaultDuration) option.selected = true;
      rentalDurationSelect.appendChild(option);
    });
  }

  updateXfRentalPrice(root);
}

function initializeXfRentalBuyoutForm(root) {
  const data = parseXfJson(root, '[data-buyout-variants-json]', { variants: [] });
  const buyNowButton = root.querySelector('[data-buy-now]');
  const buyoutPrice = root.querySelector('[data-buyout-price]');
  const currentPrimaryValue = root.dataset.currentPrimaryValue || '';

  const buyoutVariant = data.variants.find((variant) => {
    if (!variant.isBuyout) return false;
    if (!currentPrimaryValue) return true;
    return variant.primaryValue === currentPrimaryValue;
  });

  if (!buyNowButton) return;

  if (buyoutVariant?.available) {
    buyNowButton.dataset.buyoutVariantId = buyoutVariant.id;
    buyNowButton.disabled = false;
    if (buyoutPrice && buyoutVariant.priceFormatted) buyoutPrice.textContent = buyoutVariant.priceFormatted;
  }
}

function initializeXfRentalQuickAdd(container = document) {
  container.querySelectorAll('.rental-product-form-block').forEach(initializeXfRentalProductForm);
  container.querySelectorAll('.rental-buyout-form-block').forEach(initializeXfRentalBuyoutForm);
}

async function addXfRentalItemToCart(root, checkout = false) {
  hideXfRentalMessage(root, '[data-rental-message]');

  const rentalDurationSelect = root.querySelector('[data-rental-duration-select]');
  const startDateInput = root.querySelector('[data-rental-start-date]');
  const endDateInput = root.querySelector('[data-rental-end-date]');

  if (!(rentalDurationSelect instanceof HTMLSelectElement) || !rentalDurationSelect.value) {
    showXfRentalMessage(root, '[data-rental-message]', 'Please select a rental duration.', 'error');
    return;
  }

  if (!(startDateInput instanceof HTMLInputElement) || !startDateInput.value) {
    showXfRentalMessage(root, '[data-rental-message]', 'Please select a start date.', 'error');
    startDateInput?.focus();
    return;
  }

  updateXfRentalEndDate(root, false);
  if (endDateInput instanceof HTMLInputElement && !endDateInput.value) {
    updateXfRentalEndDate(root, true);
  }

  const selectedOption = rentalDurationSelect.options[rentalDurationSelect.selectedIndex];
  const sellingPlanId = selectedOption?.dataset.sellingPlanId || null;
  const durationLabel = selectedOption?.dataset.durationLabel || selectedOption?.textContent?.trim() || '';
  const buttons = root.querySelectorAll('[data-rent-now], [data-add-rental-to-cart]');

  buttons.forEach((button) => {
    if (button instanceof HTMLButtonElement) button.disabled = true;
  });

  const payload = {
    id: Number(rentalDurationSelect.value),
    quantity: getXfRentalQuantity(root),
    properties: {
      'Rental Type': 'Monthly Rental',
      'Rental Duration': durationLabel,
      'Start Date': startDateInput.value,
      'End Date': endDateInput?.value || '',
    },
  };

  if (sellingPlanId) payload.selling_plan = Number(sellingPlanId);

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || error.message || 'Unable to add item to cart.');
    }

    if (checkout) {
      if (window.XFurnitureInstallationDeliveryFee?.sync) {
        await window.XFurnitureInstallationDeliveryFee.sync();
      }
      window.location.assign('/checkout');
      return;
    }

    let cart = null;
    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartResponse.ok) cart = await cartResponse.json();
    } catch (error) {
      cart = null;
    }

    root.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: root.id,
          data: {
            source: 'xf-rental-quick-add',
            itemCount: cart?.item_count || payload.quantity,
            productId: root.dataset.productId,
            variantId: String(payload.id),
          },
        },
      })
    );

    showXfRentalMessage(root, '[data-rental-message]', 'Added to cart.', 'success');
    document.querySelector('cart-drawer-component')?.open?.();
  } catch (error) {
    showXfRentalMessage(root, '[data-rental-message]', error.message || 'Unable to add item to cart.', 'error');
  } finally {
    buttons.forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    });
  }
}

async function buyXfRentalOut(root) {
  hideXfRentalMessage(root, '[data-buyout-message]');

  const buyNowButton = root.querySelector('[data-buy-now]');
  const buyoutVariantId = buyNowButton?.dataset.buyoutVariantId ? Number(buyNowButton.dataset.buyoutVariantId) : null;

  if (!buyoutVariantId) {
    showXfRentalMessage(root, '[data-buyout-message]', 'Buy out option is unavailable.', 'error');
    return;
  }

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: buyoutVariantId,
        quantity: 1,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || error.message || 'Unable to add item to cart.');
    }

    window.location.assign('/checkout');
  } catch (error) {
    showXfRentalMessage(root, '[data-buyout-message]', error.message || 'Unable to add item to cart.', 'error');
  }
}

if (!window.__xfRentalQuickAddBound) {
  window.__xfRentalQuickAddBound = true;

  document.addEventListener('change', (event) => {
    const root = event.target?.closest?.('.rental-product-form-block');
    if (!root) return;

    if (event.target.matches('[data-rental-duration-select]')) {
      updateXfRentalPrice(root);
      updateXfRentalEndDate(root, false);
    }

    if (event.target.matches('[data-rental-start-date]')) {
      updateXfRentalEndDate(root, false);
    }
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target?.closest?.('[data-qty-minus], [data-qty-plus]');
    if (quantityButton) {
      const root = quantityButton.closest('.rental-product-form-block');
      const quantityInput = root?.querySelector('[data-rental-quantity]');

      if (root && quantityInput instanceof HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        const currentValue = getXfRentalQuantity(root);
        quantityInput.value = quantityButton.matches('[data-qty-minus]')
          ? String(Math.max(1, currentValue - 1))
          : String(currentValue + 1);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const rentButton = event.target?.closest?.('[data-rent-now], [data-add-rental-to-cart]');
    if (rentButton) {
      const root = rentButton.closest('.rental-product-form-block');
      if (root) {
        event.preventDefault();
        event.stopPropagation();
        addXfRentalItemToCart(root, rentButton.matches('[data-rent-now]'));
      }
      return;
    }

    const buyoutButton = event.target?.closest?.('[data-buy-now]');
    if (buyoutButton) {
      const root = buyoutButton.closest('.rental-buyout-form-block');
      if (root) {
        event.preventDefault();
        event.stopPropagation();
        buyXfRentalOut(root);
      }
    }
  });

  initializeXfRentalQuickAdd(document);
}
